// src/pages/healthcare/consultation/consultationComponents/EndConsultModal.tsx

import React, { useState, useEffect } from "react";
import { Modal, Button, Steps, Select, Spin, Card, Divider, Checkbox, message, Tabs, Collapse, Tag, Progress, Space, Typography, Alert, Result } from "antd";
import { CheckCircleOutlined, FileTextOutlined, FilePdfOutlined, CheckSquareOutlined, SaveOutlined, CloseOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getSessionUser } from "../../../../utils/auth/sessionUser";

import { Appointment, CompletedAppointment } from "../../../../types/appointments/AppointmentTypes";
import { SpeakerMap, Transcript } from "../../../../types/appointments/ConsultTypes";
import { ConsultChatMessage } from "../../../../types/appointments/ConsultChatTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Suggestion } from "../../../../utils/healthcare/SugestionUtils";
import { ReportTemplate } from "../../../../types/healthcare/ReportTypes";

import { fetchReportTemplates } from "../../../../api/healthcare/ReportApi";
import { generateConsultationReport, saveConsultationWithoutReport } from "../../../../api/healthcare/Consultation/ConsultationReportApi";
import { fetchConsultSession } from "../../../../api/healthcare/Consultation/ConsultApi";

const { Step } = Steps;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Text, Title } = Typography;

interface EndConsultModalProps {
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	suggestions: Suggestion[];
	transcript: Transcript[];
	isOpen: boolean;
	closeModal: () => void;
	onFinalise: () => Promise<boolean>;
	appointment: Appointment;
	consult: CompletedAppointment;
	speakerMap?: SpeakerMap;
	roomId?: string;
	chat?: ConsultChatMessage[];
}

const EndConsultModal: React.FC<EndConsultModalProps> = ({ patient, setPatient, isOpen, closeModal, onFinalise, transcript, suggestions, appointment, consult, speakerMap, roomId, chat }) => {
	const [activeStep, setActiveStep] = useState<number>(0);
	const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

	const [templates, setTemplates] = useState<ReportTemplate[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
	const [generateReport, setGenerateReport] = useState<boolean>(true);
	const [generatedReport, setGeneratedReport] = useState<string>("");
	const [isGenerating, setIsGenerating] = useState<boolean>(false);
	const [generationProgress, setGenerationProgress] = useState<number>(0);

	const [diagnosis, setDiagnosis] = useState<string>("");
	const [consultationNotes, setConsultationNotes] = useState<string>("");
	const [consultGuidance, setConsultGuidance] = useState<any>(null);

	const [checklistItems, setChecklistItems] = useState<{ id: string; text: string; checked: boolean; required: boolean }[]>([
		{ id: "diagnosis", text: "Final diagnosis documented", checked: false, required: false },
		{ id: "notes", text: "Consultation notes completed", checked: false, required: false },
		{ id: "medications", text: "Medications reviewed", checked: false, required: false },
		{ id: "tests", text: "Tests ordered (if applicable)", checked: false, required: false },
		{ id: "report", text: "Report generated or opted out", checked: false, required: true },
	]);

	const steps = [
		{ title: "Summary", icon: <FileTextOutlined /> },
		{ title: "Documentation", icon: <CheckCircleOutlined /> },
		{ title: "Report", icon: <FilePdfOutlined /> },
		{ title: "Complete", icon: <CheckSquareOutlined /> },
	];

	useEffect(() => {
		loadTemplates();
		loadConsultGuidance();
		extractClinicalData();
	}, []);

	const loadTemplates = async () => {
		try {
			const { data } = await fetchReportTemplates();
			setTemplates(data);
			if (data.length > 0) {
				setSelectedTemplate(data[0]);
			}
		} catch (error) {
			console.error("Error loading templates:", error);
			message.error("Failed to load report templates");
		}
	};

	const loadConsultGuidance = async () => {
		try {
			const res = await fetchConsultSession(appointment.id);
			if (res?.session) {
				setConsultGuidance(res.session.final_guidance);
			}
		} catch (error) {
			console.log("No consult guidance available");
		}
	};

	const extractClinicalData = () => {
		const diagnosisSuggestions = suggestions.filter((s) => s.type === "diagnosis" && s.status === "accepted");
		if (diagnosisSuggestions.length > 0) {
			setDiagnosis(diagnosisSuggestions[0].text);
			setChecklistItems((prev) => prev.map((item) => (item.id === "diagnosis" ? { ...item, checked: true } : item)));
		}

		if (consult.notes) {
			setConsultationNotes(consult.notes);
			setChecklistItems((prev) => prev.map((item) => (item.id === "notes" ? { ...item, checked: true } : item)));
		}

		if (patient.medications && patient.medications.length > 0) {
			setChecklistItems((prev) => prev.map((item) => (item.id === "medications" ? { ...item, checked: true } : item)));
		}

		if (patient.tests && patient.tests.length > 0) {
			setChecklistItems((prev) => prev.map((item) => (item.id === "tests" ? { ...item, checked: true } : item)));
		}
	};

	const handleGenerateReport = async () => {
		setIsGenerating(true);
		setGenerationProgress(10);
		setSaveError(null);

		try {
			if (!selectedTemplate) {
				throw new Error("No template selected");
			}

			setGenerationProgress(30);

			const currentUser = getSessionUser();
			const result = await generateConsultationReport({
				patient,
				consultation: {
					...consult,
					diagnosis,
					notes: consultationNotes,
				},
				transcripts: transcript,
				template: selectedTemplate,
				consultGuidance: consultGuidance?.executive_summary ?? null,
				suggestions,
				practitionerId: currentUser.id,
				speakerMap,
				roomId,
				chat,
				consultationAt: appointment?.start_time,
			});

			setGenerationProgress(90);

			if (result.success && result.report) {
				setGeneratedReport(result.report);
				setGenerationProgress(100);
				setChecklistItems((prev) => prev.map((item) => (item.id === "report" ? { ...item, checked: true } : item)));
				message.success("Report generated and saved successfully");

				const currentCompleted = new Set(completedSteps);
				currentCompleted.add(2);
				setCompletedSteps(currentCompleted);
			}
		} catch (error: any) {
			console.error("Error generating report:", error);
			setSaveError(error.message || "Failed to generate report");
			message.error("Failed to generate report");
		} finally {
			setIsGenerating(false);
		}
	};

	const handleSaveWithoutReport = async () => {
		setIsSaving(true);
		setSaveError(null);

		try {
			// The two ids are for talk's finalise hand-off, which the browser now makes itself —
			// they used to be derived server-side inside talk's own save endpoint, which no longer
			// exists. Read from the same cookie the report path reads, so both paths agree.
			const currentUser = getSessionUser();
			const result = await saveConsultationWithoutReport({
				consultation: {
					...consult,
					diagnosis,
					notes: consultationNotes,
				},
				transcripts: transcript,
				suggestions,
				speakerMap,
				roomId,
				chat,
				practitionerId: currentUser.id,
				patientId: patient.id,
			});

			if (result.success) {
				setChecklistItems((prev) => prev.map((item) => (item.id === "report" ? { ...item, checked: true } : item)));
				message.success("Consultation saved successfully");

				const currentCompleted = new Set(completedSteps);
				currentCompleted.add(2);
				setCompletedSteps(currentCompleted);
			}
		} catch (error: any) {
			console.error("Error saving consultation:", error);
			setSaveError(error.message || "Failed to save consultation");
			message.error("Failed to save consultation");
		} finally {
			setIsSaving(false);
		}
	};

	const finalizeConsultation = async () => {
		setIsSaving(true);
		setSaveError(null);

		let completed = false;
		try {
			completed = await onFinalise();
		} catch {
			console.error("consult.finalise.handler_failed", { appointmentId: appointment.id });
		}
		setIsSaving(false);

		if (!completed) {
			// The parent already toasted the failure; a toast here would be the second one.
			setSaveError("Failed to finalize consultation");
			return;
		}

		setSaveSuccess(true);
		setTimeout(closeModal, 2000);
	};

	const nextStep = () => {
		const currentCompleted = new Set(completedSteps);
		currentCompleted.add(activeStep);
		setCompletedSteps(currentCompleted);
		setActiveStep((prev) => prev + 1);
	};

	const prevStep = () => {
		setActiveStep((prev) => prev - 1);
	};

	const updateChecklistItem = (id: string, checked: boolean) => {
		setChecklistItems((prev) => prev.map((item) => (item.id === id ? { ...item, checked } : item)));
	};

	const getCompletionPercentage = (): number => {
		const checkedCount = checklistItems.filter((item) => item.checked).length;
		return Math.round((checkedCount / checklistItems.length) * 100);
	};

	const canProceedToNext = (): boolean => {
		switch (activeStep) {
			case 0:
				return true;
			case 1:
				return true;
			case 2:
				return checklistItems.find((item) => item.id === "report")?.checked || false;
			default:
				return true;
		}
	};

	const canFinalize = (): boolean => {
		return checklistItems.filter((item) => item.required).every((item) => item.checked);
	};

	const renderSummaryStep = () => {
		return (
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
				<Card
					size="small"
					title={
						<Space>
							<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e6f7ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
								<FileTextOutlined style={{ color: "#1890ff" }} />
							</div>
							<div>
								<div style={{ fontSize: 14, fontWeight: 500 }}>Patient Information</div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Patient demographic details
								</Text>
							</div>
						</Space>
					}
				>
					<Space direction="vertical" style={{ width: "100%" }} size="small">
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Name
								</Text>
								<div style={{ fontSize: 13 }}>{`${patient.first_name} ${patient.last_name}`}</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Date of Birth
								</Text>
								<div style={{ fontSize: 13 }}>{patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : "N/A"}</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Gender
								</Text>
								<div style={{ fontSize: 13 }}>{patient.gender || "N/A"}</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Contact
								</Text>
								<div style={{ fontSize: 13 }}>{patient.phone || "N/A"}</div>
							</div>
						</div>

						<Divider style={{ margin: "12px 0" }} />

						<div>
							<Text strong style={{ fontSize: 12 }}>
								Appointment Details
							</Text>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
								<div>
									<Text type="secondary" style={{ fontSize: 11 }}>
										Date & Time
									</Text>
									<div style={{ fontSize: 13 }}>{appointment.start_time ? new Date(appointment.start_time).toLocaleString() : "N/A"}</div>
								</div>
								<div>
									<Text type="secondary" style={{ fontSize: 11 }}>
										Type
									</Text>
									<div style={{ fontSize: 13 }}>{appointment.category || "Standard Consultation"}</div>
								</div>
							</div>
						</div>
					</Space>
				</Card>

				<Card
					size="small"
					title={
						<Space>
							<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff7e6", display: "flex", alignItems: "center", justifyContent: "center" }}>
								<CheckCircleOutlined style={{ color: "#fa8c16" }} />
							</div>
							<div>
								<div style={{ fontSize: 14, fontWeight: 500 }}>Consultation Summary</div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Key metrics from this consultation
								</Text>
							</div>
						</Space>
					}
				>
					<Space direction="vertical" style={{ width: "100%" }} size="small">
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Transcript Entries
							</Text>
							<div style={{ fontSize: 13 }}>{transcript.length} entries recorded</div>
						</div>
						{chat && chat.length > 0 && (
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Chat Archived
								</Text>
								<div style={{ fontSize: 13 }}>{chat.length} messages</div>
							</div>
						)}
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								AI Suggestions
							</Text>
							<div style={{ fontSize: 13 }}>{suggestions.length} suggestions generated</div>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Accepted Suggestions
							</Text>
							<div style={{ fontSize: 13 }}>{suggestions.filter((s) => s.status === "accepted").length} accepted</div>
						</div>

						{consultGuidance && (
							<>
								<Divider style={{ margin: "8px 0" }} />
								<div>
									<Text type="secondary" style={{ fontSize: 11 }}>
										Consultation Guidance
									</Text>
									<div style={{ fontSize: 13 }}>
										<Tag color="purple">Available</Tag>
									</div>
								</div>
							</>
						)}
					</Space>
				</Card>

				{/* <Card
					size="small"
					title={
						<Space>
							<CheckCircleOutlined style={{ color: "#52c41a" }} />
							<Text strong>Top Suggestions</Text>
						</Space>
					}
					style={{ gridColumn: "1 / -1" }}
				>
					<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
						<div style={{ padding: 12, border: "1px solid var(--border-color)", borderRadius: 8 }}>
							<Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
								Diagnoses
							</Text>
							<Space direction="vertical" style={{ width: "100%" }} size="small">
								{suggestions
									.filter((s) => s.type === "diagnosis" && s.status === "accepted")
									.slice(0, 3)
									.map((s, i) => (
										<div key={i} style={{ paddingBottom: 8, borderBottom: "1px solid var(--border-color)" }}>
											<div style={{ fontSize: 12, fontWeight: 500 }}>{s.text}</div>
											<Tag color="success" style={{ marginTop: 4 }}>
												Confidence: {Math.round(s.confidence * 100)}%
											</Tag>
										</div>
									))}
								{suggestions.filter((s) => s.type === "diagnosis" && s.status === "accepted").length === 0 && <Text type="secondary">No diagnoses accepted</Text>}
							</Space>
						</div>

						<div style={{ padding: 12, border: "1px solid var(--border-color)", borderRadius: 8 }}>
							<Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
								Treatments
							</Text>
							<Space direction="vertical" style={{ width: "100%" }} size="small">
								{suggestions
									.filter((s) => s.type === "treatment" && s.status === "accepted")
									.slice(0, 3)
									.map((s, i) => (
										<div key={i} style={{ paddingBottom: 8, borderBottom: "1px solid var(--border-color)" }}>
											<div style={{ fontSize: 12, fontWeight: 500 }}>{s.text}</div>
											<Text type="secondary" style={{ fontSize: 11 }}>
												{s.why.substring(0, 60)}...
											</Text>
										</div>
									))}
								{suggestions.filter((s) => s.type === "treatment" && s.status === "accepted").length === 0 && <Text type="secondary">No treatments accepted</Text>}
							</Space>
						</div>

						<div style={{ padding: 12, border: "1px solid var(--border-color)", borderRadius: 8 }}>
							<Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
								Investigations
							</Text>
							<Space direction="vertical" style={{ width: "100%" }} size="small">
								{suggestions
									.filter((s) => s.type === "investigation" && s.status === "accepted")
									.slice(0, 3)
									.map((s, i) => (
										<div key={i} style={{ paddingBottom: 8, borderBottom: "1px solid var(--border-color)" }}>
											<div style={{ fontSize: 12, fontWeight: 500 }}>{s.text}</div>
											<Text type="secondary" style={{ fontSize: 11 }}>
												{s.why.substring(0, 60)}...
											</Text>
										</div>
									))}
								{suggestions.filter((s) => s.type === "investigation" && s.status === "accepted").length === 0 && <Text type="secondary">No investigations accepted</Text>}
							</Space>
						</div>
					</div>
				</Card> */}
			</div>
		);
	};

	const renderDocumentationStep = () => {
		return (
			<Space direction="vertical" style={{ width: "100%" }} size="middle">
				<div>
					<Space>
						<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e6f7ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
							<FileTextOutlined style={{ color: "#1890ff" }} />
						</div>
						<div>
							<div style={{ fontSize: 16, fontWeight: 500 }}>Clinical Documentation</div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Complete documentation for this consultation
							</Text>
						</div>
					</Space>
				</div>

				<Space direction="vertical" style={{ width: "100%" }}>
					<div>
						<Text strong>Final Diagnosis</Text>
						<ReactQuill
							value={diagnosis}
							onChange={(value) => {
								setDiagnosis(value);
								updateChecklistItem("diagnosis", value.replace(/<[^>]*>/g, "").trim().length > 0);
							}}
						/>
					</div>
					<div>
						<Text strong>Final Consultation Notes</Text>
						<ReactQuill
							value={consultationNotes}
							onChange={(value) => {
								setConsultationNotes(value);
								updateChecklistItem("notes", value.replace(/<[^>]*>/g, "").trim().length > 0);
							}}
						/>
					</div>
				</Space>
			</Space>
		);
	};

	const renderReportStep = () => {
		return (
			<Space direction="vertical" style={{ width: "100%" }} size="middle">
				<div>
					<Space>
						<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
							<FilePdfOutlined />
						</div>
						<div>
							<div style={{ fontSize: 16, fontWeight: 500 }}>Consultation Report</div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Generate or skip report generation
							</Text>
						</div>
					</Space>
				</div>

				{saveError && <Alert message="Error" description={saveError} type="error" closable onClose={() => setSaveError(null)} />}

				<div>
					<Checkbox
						checked={generateReport}
						onChange={(e) => {
							setGenerateReport(e.target.checked);
							if (!e.target.checked) {
								setChecklistItems((prev) => prev.map((item) => (item.id === "report" ? { ...item, checked: false } : item)));
							}
						}}
					>
						<Text strong>Generate consultation report</Text>
					</Checkbox>
				</div>

				{generateReport ? (
					<div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16 }}>
						<Space direction="vertical" style={{ width: "100%" }}>
							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Report Template <span style={{ color: "#ff4d4f" }}>*</span>
								</Text>
								<Select
									style={{ width: "100%" }}
									value={selectedTemplate?.id}
									onChange={(value) => {
										const template = templates.find((t) => t.id === value);
										if (template) setSelectedTemplate(template);
									}}
									options={templates.map((t) => ({ label: t.name, value: t.id }))}
									placeholder="Select a template"
									disabled={isGenerating || isSaving}
								/>
							</div>

							<Button
								type="primary"
								icon={isGenerating ? <LoadingOutlined /> : <FilePdfOutlined />}
								onClick={handleGenerateReport}
								loading={isGenerating}
								disabled={!selectedTemplate || isGenerating || isSaving || checklistItems.find((item) => item.id === "report")?.checked}
								block
							>
								{checklistItems.find((item) => item.id === "report")?.checked ? "Report Generated" : "Generate Report"}
							</Button>

							{isGenerating && (
								<div>
									<Text style={{ fontSize: 12, display: "block", marginBottom: 8 }}>Generation Progress</Text>
									<Progress percent={generationProgress} size="small" />
									<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
										Processing consultation data...
									</Text>
								</div>
							)}

							{selectedTemplate && (
								<Collapse ghost>
									<Panel header="Template Preview" key="1">
										<div style={{ fontSize: 11, color: "var(--text-color-secondary)" }} dangerouslySetInnerHTML={{ __html: selectedTemplate.body.substring(0, 500) + "..." }} />
									</Panel>
								</Collapse>
							)}
						</Space>

						<div>
							<Text strong style={{ display: "block", marginBottom: 8 }}>
								Generated Report
							</Text>
							<div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 16, background: "#fff", minHeight: 500, maxHeight: 500, overflow: "auto" }}>
								{!generatedReport && !isGenerating ? (
									<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
										<FilePdfOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
										<Text type="secondary">Select a template and click "Generate Report"</Text>
									</div>
								) : isGenerating ? (
									<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
										<Spin size="large" />
										<Text type="secondary" style={{ marginTop: 16 }}>
											Generating report...
										</Text>
									</div>
								) : (
									<div dangerouslySetInnerHTML={{ __html: generatedReport }} />
								)}
							</div>
						</div>
					</div>
				) : (
					<Card>
						<Space direction="vertical" style={{ width: "100%" }} align="center">
							<WarningOutlined style={{ fontSize: 48, color: "#faad14" }} />
							<Text strong>No Report Generation</Text>
							<Text type="secondary">The consultation will be saved without generating a formal report.</Text>
							<Button type="primary" onClick={handleSaveWithoutReport} loading={isSaving} disabled={isSaving || checklistItems.find((item) => item.id === "report")?.checked}>
								{checklistItems.find((item) => item.id === "report")?.checked ? "Consultation Saved" : "Save Consultation"}
							</Button>
						</Space>
					</Card>
				)}
			</Space>
		);
	};

	const renderCompletionStep = () => {
		const completionPercentage = getCompletionPercentage();

		if (saveSuccess) {
			return (
				<Result
					status="success"
					title="Consultation Completed Successfully"
					subTitle="The consultation has been finalized and all data has been saved."
					extra={[
						<Button type="primary" key="close" onClick={closeModal}>
							Close
						</Button>,
					]}
				/>
			);
		}

		return (
			<Card size="small">
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<Space>
							<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f6ffed", display: "flex", alignItems: "center", justifyContent: "center" }}>
								<CheckCircleOutlined style={{ color: "#52c41a" }} />
							</div>
							<div>
								<div style={{ fontSize: 16, fontWeight: 500 }}>Completion Checklist</div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Verify all required items are completed
								</Text>
							</div>
						</Space>
					</div>

					{saveError && <Alert message="Error Finalizing Consultation" description={saveError} type="error" closable onClose={() => setSaveError(null)} />}

					<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
						<Space direction="vertical" style={{ width: "100%" }}>
							{checklistItems.map((item) => (
								<div key={item.id} style={{ padding: 12, border: "1px solid var(--border-color)", borderRadius: 8 }}>
									<Space>
										<Checkbox checked={item.checked} disabled />
										<div>
											<Text strong style={{ fontSize: 13 }}>
												{item.text}
												{item.required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
											</Text>
										</div>
									</Space>
								</div>
							))}
							<Text type="secondary" style={{ fontSize: 12 }}>
								<span style={{ color: "#ff4d4f" }}>*</span> Required items must be completed before finalizing
							</Text>
						</Space>

						<Card size="small" style={{ height: "fit-content" }}>
							<Space direction="vertical" style={{ width: "100%" }} align="center">
								<Text strong>Completion Status</Text>
								<div style={{ position: "relative", width: 120, height: 120 }}>
									<Progress type="circle" percent={completionPercentage} strokeColor={completionPercentage === 100 ? "#52c41a" : "#1890ff"} width={120} />
								</div>
								<div style={{ width: "100%" }}>
									<Divider style={{ margin: "12px 0" }} />
									<Space direction="vertical" style={{ width: "100%" }} size="small">
										<div style={{ display: "flex", justifyContent: "space-between" }}>
											<Text>Total Items:</Text>
											<Text strong>{checklistItems.length}</Text>
										</div>
										<div style={{ display: "flex", justifyContent: "space-between" }}>
											<Text>Completed:</Text>
											<Text strong>{checklistItems.filter((item) => item.checked).length}</Text>
										</div>
										<div style={{ display: "flex", justifyContent: "space-between" }}>
											<Text>Required:</Text>
											<Text strong>
												{checklistItems.filter((item) => item.required && item.checked).length}/{checklistItems.filter((item) => item.required).length}
											</Text>
										</div>
									</Space>
								</div>
								<Button type="primary" icon={<CheckCircleOutlined />} block disabled={!canFinalize() || isSaving} loading={isSaving} onClick={finalizeConsultation} style={{ marginTop: 12 }}>
									Finalize Consultation
								</Button>
								{!canFinalize() && (
									<Text type="danger" style={{ fontSize: 11, textAlign: "center" }}>
										Please complete all required items before finalizing
									</Text>
								)}
							</Space>
						</Card>
					</div>
				</Space>
			</Card>
		);
	};

	const renderStepContent = () => {
		switch (activeStep) {
			case 0:
				return renderSummaryStep();
			case 1:
				return renderDocumentationStep();
			case 2:
				return renderReportStep();
			case 3:
				return renderCompletionStep();
			default:
				return null;
		}
	};

	return (
		<Modal
			title="Complete Consultation"
			open={isOpen}
			onCancel={saveSuccess ? closeModal : undefined}
			width="95vw"
			style={{ top: 20, marginBottom: 20 }}
			zIndex={2100}
			footer={
				saveSuccess
					? null
					: [
							<Button key="back" onClick={prevStep} disabled={activeStep === 0 || isSaving}>
								Back
							</Button>,
							activeStep === 3 ? null : (
								<Button key="next" type="primary" onClick={nextStep} disabled={!canProceedToNext() || isSaving}>
									Next
								</Button>
							),
						]
			}
			closable={!isSaving}
			maskClosable={false}
		>
			<div style={{ marginBottom: 24 }}>
				<Steps current={activeStep}>
					{steps.map((step, index) => (
						<Step
							key={index}
							title={step.title}
							icon={step.icon}
							onClick={() => {
								if (!isSaving && (completedSteps.has(index) || index < activeStep)) {
									setActiveStep(index);
								}
							}}
							style={{ cursor: !isSaving && (completedSteps.has(index) || index < activeStep) ? "pointer" : "default" }}
						/>
					))}
				</Steps>
			</div>

			{renderStepContent()}
		</Modal>
	);
};

export default EndConsultModal;
